/**
 * MomentsView — coordination tab inside Manifest.
 *
 * Two sections:
 *   1. "In your cohort" — open Moments (planning + confirmed) among your
 *      opted-in host_university + semester peers.
 *   2. "Recommended for you" — Moments from OTHER cohorts you've been surfaced
 *      to via smart targeting (your match tier overlap explained inline).
 *
 * Plus a "Create Moment" form and a lightweight detail modal for join +
 * date voting + creator lock/complete/cancel actions.
 *
 * Discipline note: no addresses, no phone numbers, no exact flight details
 * ever collected or displayed. Budgets are raw numbers set by the creator.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MOMENT_TAGLINE, START_MOMENT_LABEL } from "@/lib/momentCopy";
import {
  Plus, X, MapPin, Users, Calendar, CalendarClock, Check, Lock,
  Sparkles, Home, Plane, Coffee, ArrowRight, Flag,
} from "lucide-react";

const TYPE_META = {
  arrival:  { label: "Arrival",   icon: Plane,     hint: "First-day logistics. Landing, taxi, dinner." },
  roommate: { label: "Roommate",  icon: Home,      hint: "Housing search. Flat viewings, roommate hunt." },
  trip:     { label: "Trip",      icon: MapPin,    hint: "Weekend or short trip. 2–3 days." },
  social:   { label: "Social",    icon: Coffee,    hint: "Dinner, coffee, city walk. A couple hours." },
};

const STATUS_META = {
  planning:  { label: "Planning",  cls: "bg-[color:var(--moe-yolk)]/40 border-[color:var(--moe-yolk)]" },
  confirmed: { label: "Confirmed", cls: "bg-[color:var(--moe-mint)] border-elec/40" },
  cancelled: { label: "Cancelled", cls: "bg-ink/8 border-ink/20 text-muted" },
  completed: { label: "Completed", cls: "bg-ink/8 border-ink/20 text-muted" },
};

// iter81 (Item P) - Roommate-type Moments relabel the trip lifecycle to
// something honest for housing searches. Same underlying statuses (so all
// existing endpoints/tests keep working), just different words.
const ROOMMATE_STATUS_META = {
  planning:  { label: "Still searching", cls: "bg-[color:var(--moe-yolk)]/40 border-[color:var(--moe-yolk)]" },
  confirmed: { label: "Still searching", cls: "bg-[color:var(--moe-yolk)]/40 border-[color:var(--moe-yolk)]" },
  cancelled: { label: "Search closed",   cls: "bg-ink/8 border-ink/20 text-muted" },
  completed: { label: "Found a place",   cls: "bg-[color:var(--moe-mint)] border-elec/40 text-ink" },
};

const ROOM_TYPE_LABELS = {
  private_room: "Private room in flatshare",
  shared_room:  "Shared room",
  studio:       "Studio",
  flatshare:    "Whole flatshare",
  any:          "Any of the above",
};

function _statusMeta(m) {
  if (m?.type === "roommate") return ROOMMATE_STATUS_META[m.status] || STATUS_META[m.status];
  return STATUS_META[m?.status] || { label: m?.status, cls: "" };
}

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00Z" : "")).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch { return iso; }
}

// iter92 (Item 2) · Honest, calm countdown for Moment cards.
// - Uses REAL start_date + optional end_date; returns null when there's no
//   date so the caller renders nothing (never fabricates a placeholder).
// - No urgency colour tricks - the return value is plain text, styled by the
//   caller in the same muted tone as other card metadata.
// - Roommate Moments intentionally skip the countdown - "move-in windows" are
//   open-ended by design (iter81 · Item P) and a "starts in X days" line
//   would misrepresent the semantics.
function _momentCountdown(m) {
  if (!m || !m.start_date) return null;
  if (m.type === "roommate") return null;
  const startMs = Date.parse(m.start_date + (m.start_date.length === 10 ? "T00:00:00Z" : ""));
  if (Number.isNaN(startMs)) return null;
  const endMs = m.end_date
    ? Date.parse(m.end_date + (m.end_date.length === 10 ? "T00:00:00Z" : ""))
    : startMs;
  const now = Date.now();
  const MS_DAY = 24 * 60 * 60 * 1000;
  // Round to UTC midnight for a stable day-count regardless of local clock.
  const dayDiffFrom = (ms) => Math.round((ms - now) / MS_DAY);
  const daysToStart = dayDiffFrom(startMs);
  const daysToEnd   = dayDiffFrom(endMs);
  if (daysToStart > 1) return `Starts in ${daysToStart} days`;
  if (daysToStart === 1) return "Starts tomorrow";
  if (daysToStart === 0) return "Starts today";
  if (daysToStart < 0 && daysToEnd >= 0) {
    // Trip is currently underway
    return daysToStart === -1 ? "Started yesterday" : `Started ${-daysToStart} days ago`;
  }
  // Fully in the past
  const daysAgo = -daysToEnd;
  if (daysAgo === 0) return "Ended today";
  if (daysAgo === 1) return "Ended yesterday";
  return `Ended ${daysAgo} days ago`;
}

// ----------------------- top-level view -----------------------
export default function MomentsView({ profile }) {
  const [searchParams] = useSearchParams();
  const [cohort, setCohort] = useState(null);   // {moments, cohort_key}
  const [inbox,  setInbox]  = useState(null);   // {moments}
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  // Deep-link support: /dashboard/manifest?tab=moments&open=<moment_id> auto-
  // opens the target Moment card once data has loaded. Used by Roam after it
  // creates a trip Moment, so the user lands directly inside the just-created
  // coordination card instead of on the tab index.
  useEffect(() => {
    const target = searchParams.get("open");
    if (target && (cohort || inbox)) setOpenId(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohort, inbox]);

  const load = async () => {
    try {
      const [c, i] = await Promise.all([
        api.get("/manifest/moments"),
        api.get("/manifest/moments/inbox/mine"),
      ]);
      setCohort(c.data);
      setInbox(i.data);
    } catch (e) {
      // If not opted in, the endpoint 403s; render nothing.
      setCohort({ moments: [], cohort_key: null });
      setInbox({ moments: [] });
    }
  };

  useEffect(() => { if (profile?.opt_in_matching) load(); }, [profile?.opt_in_matching]);

  if (!profile?.opt_in_matching) {
    // iter73 (P0) — the previous inline "Opt in to Manifest matching to
    // create and join Moments." card was a dead-end: it named the
    // dependency but offered no click-through. Replace with a proper CTA
    // card that ships users straight to the Matches tab where the opt-in
    // button lives, so the gate is honest AND actionable.
    return (
      <div data-testid="moments-needs-optin" className="moe-card p-6 md:p-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Assembly is gated</div>
        <div className="mt-1 text-lg md:text-xl font-bold text-ink leading-snug">
          You need to be visible to your cohort before you can coordinate with them.
        </div>
        <p className="mt-1 text-sm text-muted leading-snug max-w-xl">
          Assemblies are group trips, arrivals, and dinners you plan with peers heading to the same country as you. To create or join one, opt in to Cohort matching first. It takes about 10 seconds, only shares your first name and host city, and you can turn it off anytime.
        </p>
        <Link
          to="?tab=matches"
          data-testid="moments-needs-optin-cta"
          className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90"
        >
          Go to Matches to opt in <ArrowRight size={13}/>
        </Link>
      </div>
    );
  }
  if (!cohort || !inbox) return <div className="text-sm text-muted">Loading Assembly…</div>;

  return (
    <div data-testid="moments-root" className="space-y-8">
      {/* ---- CREATE ---- */}
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-elec font-bold">Assembly</div>
          <h2 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight">Coordinate real things.</h2>
          <p className="mt-1 text-sm text-muted max-w-xl leading-snug">
            {MOMENT_TAGLINE}
          </p>
          <p className="mt-1 text-[12px] text-muted/80 max-w-xl leading-snug">
            Intent-only. No pooled money, no addresses.
          </p>
        </div>
        <button
          type="button"
          data-testid="moments-create-open"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90"
        >
          <Plus size={14} strokeWidth={2.4} /> {START_MOMENT_LABEL}
        </button>
      </div>

      {creating && (
        <CreateForm
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); toast.success("Assembly created"); }}
        />
      )}

      {/* ---- INBOX (recommended) ---- */}
      {inbox.moments.length > 0 && (
        <section data-testid="moments-inbox">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
            <Sparkles size={12} strokeWidth={2.4} /> Recommended for you
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {inbox.moments.map((m) => (
              <MomentCard key={m.id} moment={m} recommended onOpen={() => setOpenId(m.id)} />
            ))}
          </div>
        </section>
      )}

      {/* ---- COHORT ---- iter81 (Item Q) - Roommate Moments get their own
         "Housing search" section at the top of the cohort feed, since
         cohort-wide visibility matters most for this type specifically.
         Everything else (arrival, trip, social) sits below in a single
         "Upcoming trips & social" section, still sorted by soonest date. */}
      <section data-testid="moments-cohort">
        {(() => {
          const roommates = cohort.moments.filter(m => m.type === "roommate");
          const others    = cohort.moments.filter(m => m.type !== "roommate");
          const activeRoommates = roommates.filter(m => m.status !== "completed" && m.status !== "cancelled");
          if (cohort.moments.length === 0) {
            return (
              <>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
                  <Users size={12} strokeWidth={2.4} /> In your cohort
                </div>
                <div data-testid="moments-cohort-empty" className="py-6 text-center text-[12px] text-muted italic">
                  No open Assemblies yet in your cohort — be the first to start one.
                </div>
              </>
            );
          }
          return (
            <>
              {/* Housing search section - only rendered when there's at least
                 one active roommate Moment. Zero-state shows nothing (no
                 fake "be the first" prompt). */}
              {activeRoommates.length > 0 && (
                <div className="mb-8" data-testid="moments-cohort-housing">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
                    <Home size={12} strokeWidth={2.4} /> Housing search
                    <span className="ml-1 text-[10px] uppercase tracking-widest text-muted font-bold">
                      · {activeRoommates.length} active
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-muted leading-snug max-w-2xl">
                    Every open flat / roommate search in your cohort - not just from people you already match with. Housing works best with the widest possible visibility.
                  </div>
                  <div className="mt-3 grid md:grid-cols-2 gap-3">
                    {activeRoommates.map((m) => (
                      <MomentCard key={m.id} moment={m} onOpen={() => setOpenId(m.id)} />
                    ))}
                  </div>
                  {/* Show closed / found-a-place searches collapsed below */}
                  {roommates.length > activeRoommates.length && (
                    <div className="mt-2 text-[11px] text-muted" data-testid="moments-cohort-housing-closed">
                      {roommates.length - activeRoommates.length} closed search{roommates.length - activeRoommates.length === 1 ? "" : "es"} (found a place or search closed)
                    </div>
                  )}
                </div>
              )}

              {/* Everything else (arrival, trip, social) - still sorted by
                 soonest start_date, but visually decoupled from housing. */}
              {others.length > 0 && (
                <div data-testid="moments-cohort-other">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
                    <Users size={12} strokeWidth={2.4} /> Upcoming trips & social
                  </div>
                  <div className="mt-3 grid md:grid-cols-2 gap-3">
                    {others.map((m) => (
                      <MomentCard key={m.id} moment={m} onOpen={() => setOpenId(m.id)} />
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </section>

      {openId && (
        <DetailModal
          momentId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ----------------------- moment card -----------------------
function MomentCard({ moment: m, recommended = false, onOpen }) {
  const Icon = TYPE_META[m.type]?.icon || Sparkles;
  const going = m.counts?.going ?? 0;
  const cap = m.capacity;
  return (
    <button
      type="button"
      data-testid={`moment-card-${m.id}`}
      onClick={onOpen}
      className="text-left moe-card p-5 hover:border-elec/50 transition-colors moe-hover-lift"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-elec font-bold">
            <Icon size={11} strokeWidth={2.4}/> {TYPE_META[m.type]?.label || m.type}
          </div>
          <div className="mt-1 text-base font-bold text-ink leading-snug break-words">{m.title}</div>
        </div>
        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${_statusMeta(m).cls || ""}`}>
          {_statusMeta(m).label}
        </span>
      </div>
      <div className="mt-2.5 flex items-center gap-3 text-[12px] text-muted flex-wrap">
        <span className="inline-flex items-center gap-1"><Calendar size={11} strokeWidth={2.4}/>{_fmtDate(m.start_date)}{m.end_date && m.end_date !== m.start_date ? ` – ${_fmtDate(m.end_date)}` : ""}</span>
        <span className="inline-flex items-center gap-1"><Users size={11} strokeWidth={2.4}/>{going}{cap ? ` of ${cap}` : ""} going</span>
        {(() => {
          // iter92 (Item 2) — real countdown, hidden when no date or when
          // roommate type (open-ended by design). No urgency red styling —
          // reads as calm supporting info in the same muted tone.
          const cd = _momentCountdown(m);
          if (!cd) return null;
          return (
            <span data-testid={`moment-countdown-${m.id}`} className="inline-flex items-center gap-1 text-elec">
              <CalendarClock size={11} strokeWidth={2.4}/>{cd}
            </span>
          );
        })()}
      </div>
      <div className="mt-2.5 text-[11px] text-muted">
        Created by <span className="text-ink font-semibold">{m.creator?.display_name || "Student"}</span>
      </div>
      {recommended && m.recommendation && (
        <div data-testid={`moment-card-reason-${m.id}`} className="mt-3 pt-3 border-t border-ink/8 flex items-start gap-2 text-[11px] text-elec">
          <Sparkles size={11} strokeWidth={2.4} className="shrink-0 mt-0.5"/>
          <span className="leading-snug">{m.recommendation.reason}</span>
        </div>
      )}
    </button>
  );
}

// ----------------------- create form -----------------------
function CreateForm({ onClose, onCreated }) {
  const [type, setType] = useState("trip");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [flex, setFlex] = useState(false);
  const [opt1, setOpt1] = useState("");
  const [opt2, setOpt2] = useState("");
  const [opt3, setOpt3] = useState("");
  const [capacity, setCapacity] = useState("");
  const [bmin, setBmin] = useState("");
  const [bmax, setBmax] = useState("");
  // iter81 (Item O) - arrival-only fields
  const [airport, setAirport] = useState("");
  const [landingTime, setLandingTime] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  // iter81 (Item P) - roommate-only fields
  const [budgetMinM, setBudgetMinM] = useState("");
  const [budgetMaxM, setBudgetMaxM] = useState("");
  const [prefArea, setPrefArea] = useState("");
  const [rmSought, setRmSought] = useState("");
  const [roomType, setRoomType] = useState("");
  const [busy, setBusy] = useState(false);

  const isArrival = type === "arrival";
  const isRoommate = type === "roommate";

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) { toast.error("Title too short"); return; }
    if (!start || !end) { toast.error("Add start and end dates"); return; }
    const body = {
      type, title: title.trim(), description: description.trim() || null,
      start_date: start, end_date: end,
      date_precision: flex ? "flexible" : "exact",
      date_options: flex ? [opt1, opt2, opt3].filter(Boolean) : null,
      capacity: capacity ? parseInt(capacity, 10) : null,
      budget_min: bmin ? parseInt(bmin, 10) : null,
      budget_max: bmax ? parseInt(bmax, 10) : null,
      // Type-specific (backend ignores unrelated fields; safe to always send)
      airport:          isArrival && airport.trim() ? airport.trim() : null,
      landing_time:     isArrival && landingTime.trim() ? landingTime.trim() : null,
      flight_number:    isArrival && flightNumber.trim() ? flightNumber.trim() : null,
      budget_min_monthly: isRoommate && budgetMinM ? parseInt(budgetMinM, 10) : null,
      budget_max_monthly: isRoommate && budgetMaxM ? parseInt(budgetMaxM, 10) : null,
      preferred_area:     isRoommate && prefArea.trim() ? prefArea.trim() : null,
      roommates_sought:   isRoommate && rmSought ? parseInt(rmSought, 10) : null,
      room_type:          isRoommate && roomType ? roomType : null,
    };
    setBusy(true);
    try {
      const r = await api.post("/manifest/moments", body);
      toast.success(`Created. ${r.data.targeted_count} match${r.data.targeted_count === 1 ? "" : "es"} notified`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create Assembly");
    } finally {
      setBusy(false);
    }
  };

  const Icon = TYPE_META[type]?.icon || Sparkles;

  return (
    <form data-testid="moments-create-form" onSubmit={submit} className="moe-card p-5 md:p-6 border-2 border-elec/30 bg-white">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-elec font-bold">Create Assembly</div>
          <div className="mt-1 text-[12px] text-muted">{TYPE_META[type]?.hint}</div>
        </div>
        <button type="button" onClick={onClose} data-testid="moments-create-cancel" className="text-muted hover:text-ink"><X size={16}/></button>
      </div>

      {/* Type picker */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(TYPE_META).map(([k, meta]) => {
          const M = meta.icon;
          return (
            <button
              key={k}
              type="button"
              data-testid={`moments-type-${k}`}
              onClick={() => setType(k)}
              className={`px-3 py-2.5 rounded-lg border text-[12px] font-semibold inline-flex items-center gap-1.5 justify-center transition-colors ${
                type === k ? "bg-ink text-white border-ink" : "bg-white text-ink border-ink/20 hover:border-ink/50"
              }`}
            >
              <M size={12} strokeWidth={2.4}/> {meta.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid gap-3">
        <label>
          <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Title</span>
          <input data-testid="moments-title" value={title} onChange={e=>setTitle(e.target.value)} maxLength={120} required minLength={3}
            placeholder={type === "trip" ? "e.g. Prague weekend" : type === "arrival" ? "e.g. First-day dinner" : type === "roommate" ? "e.g. WG-Gesucht flat hunt" : "e.g. Sunday coffee"}
            className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Description (optional)</span>
          <textarea data-testid="moments-description" value={description} onChange={e=>setDescription(e.target.value)} rows={2} maxLength={2000}
            className="mt-1 block w-full px-3 py-2 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <label>
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">
              {isArrival ? "Landing date" : isRoommate ? "Move-in window · from" : "Start"}
            </span>
            <input data-testid="moments-start" type="date" value={start} onChange={e=>setStart(e.target.value)} required
              className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">
              {isArrival ? "Also arriving until (same day is fine)" : isRoommate ? "Move-in window · to" : "End"}
            </span>
            <input data-testid="moments-end" type="date" value={end} onChange={e=>setEnd(e.target.value)} required
              className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
          </label>
        </div>
        {isRoommate && (
          <div className="text-[11px] text-muted -mt-2">
            Housing searches are open-ended. This is when you're looking to move in, not a fixed trip window.
          </div>
        )}

        {/* iter81 (Item O) - Arrival-specific fields. All optional so the
           lightest possible entry "landing on day X" still works, but a
           student who wants to actually coordinate a shared cab can share
           airport + landing time. Flight number is fully optional; some
           won't want to share it. */}
        {isArrival && (
          <div className="mt-1 grid sm:grid-cols-3 gap-3" data-testid="moments-arrival-fields">
            <label>
              <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Airport (optional)</span>
              <input data-testid="moments-airport" value={airport} onChange={e=>setAirport(e.target.value)} maxLength={48}
                placeholder="e.g. MUC or Munich"
                className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
            </label>
            <label>
              <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Landing time (optional)</span>
              <input data-testid="moments-landing-time" type="time" value={landingTime} onChange={e=>setLandingTime(e.target.value)}
                className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
            </label>
            <label>
              <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Flight # (optional)</span>
              <input data-testid="moments-flight-number" value={flightNumber} onChange={e=>setFlightNumber(e.target.value)} maxLength={12}
                placeholder="e.g. LH763"
                className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
            </label>
          </div>
        )}

        {/* iter81 (Item P) - Roommate-specific fields. Logistics ONLY -
           budget, area, room type. No gender / demographic / protected-
           characteristic fields exist on this schema and none will be added. */}
        {isRoommate && (
          <div className="mt-1 grid gap-3" data-testid="moments-roommate-fields">
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Monthly budget min (€, optional)</span>
                <input data-testid="moments-budget-min-monthly" type="number" min={0} value={budgetMinM} onChange={e=>setBudgetMinM(e.target.value)}
                  className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Monthly budget max (€, optional)</span>
                <input data-testid="moments-budget-max-monthly" type="number" min={0} value={budgetMaxM} onChange={e=>setBudgetMaxM(e.target.value)}
                  className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
              </label>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Preferred area (optional)</span>
                <input data-testid="moments-preferred-area" value={prefArea} onChange={e=>setPrefArea(e.target.value)} maxLength={120}
                  placeholder="e.g. Schwabing, near U6"
                  className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Roommates sought</span>
                <input data-testid="moments-roommates-sought" type="number" min={1} max={6} value={rmSought} onChange={e=>setRmSought(e.target.value)}
                  placeholder="optional"
                  className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
              </label>
            </div>
            <label>
              <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Room type (optional)</span>
              <select data-testid="moments-room-type" value={roomType} onChange={e=>setRoomType(e.target.value)}
                className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec">
                <option value="">Any / not specified</option>
                <option value="private_room">Private room in flatshare</option>
                <option value="shared_room">Shared room</option>
                <option value="studio">Studio</option>
                <option value="flatshare">Whole flatshare</option>
                <option value="any">Any of the above</option>
              </select>
            </label>
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-[13px]">
          <input data-testid="moments-flexible" type="checkbox" checked={flex} onChange={e=>setFlex(e.target.checked)}/>
          Flexible. Propose 2–3 date options for peers to vote on
        </label>
        {flex && (
          <div className="grid sm:grid-cols-3 gap-2">
            {[[opt1, setOpt1, "moments-opt-1"], [opt2, setOpt2, "moments-opt-2"], [opt3, setOpt3, "moments-opt-3"]].map(([v, setv, tid]) => (
              <input key={tid} data-testid={tid} type="date" value={v} onChange={e=>setv(e.target.value)}
                className="h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <label>
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Capacity</span>
            <input data-testid="moments-capacity" type="number" min={2} max={200} value={capacity} onChange={e=>setCapacity(e.target.value)}
              placeholder="optional" className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Budget min</span>
            <input data-testid="moments-bmin" type="number" min={0} value={bmin} onChange={e=>setBmin(e.target.value)}
              placeholder="optional" className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Budget max</span>
            <input data-testid="moments-bmax" type="number" min={0} value={bmax} onChange={e=>setBmax(e.target.value)}
              placeholder="optional" className="mt-1 block w-full h-11 px-3 rounded-lg border border-ink/20 text-sm focus:outline-none focus:border-elec"/>
          </label>
        </div>

        <div className="text-[11px] text-muted leading-snug pt-1 border-t border-ink/8">
          Assemblies organise <span className="text-ink font-semibold">intent, not money</span>.
          Never share exact addresses, flight numbers, or phone numbers here. Those stay 1:1 in Messages.
        </div>

        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={busy} data-testid="moments-submit"
            className="h-11 px-6 rounded-lg bg-ink text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? "Creating…" : "Create Assembly"}
          </button>
          <button type="button" onClick={onClose} className="h-11 px-4 rounded-lg text-sm font-semibold text-muted hover:text-ink">Cancel</button>
        </div>
      </div>
    </form>
  );
}

// ----------------------- detail modal -----------------------
function DetailModal({ momentId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/manifest/moments/${momentId}`);
      setD(r.data);
    } catch (e) {
      toast.error("Couldn't load Assembly");
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [momentId]);

  const setState = async (state) => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/state`, { state });
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setBusy(false); }
  };

  const voteDate = async (opt) => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/date-vote`, { option_date: opt });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't vote");
    } finally { setBusy(false); }
  };

  const lockDate = async (opt) => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/lock-date`, { locked_date: opt });
      await load(); onChanged();
      toast.success("Date locked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't lock");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!confirm("Cancel this Assembly?")) return;
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/cancel`);
      onChanged(); onClose();
    } catch (e) {
      toast.error("Couldn't cancel");
    } finally { setBusy(false); }
  };

  // iter80 (Item N) - promote / demote co-organizers. Creator-only,
  // enforced server-side. UI already gates rendering on d.is_creator.
  const promoteCoOrganizer = async (caseId) => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/co-organizers/${caseId}`);
      await load();
      toast.success("Co-organizer added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote");
    } finally { setBusy(false); }
  };
  const demoteCoOrganizer = async (caseId) => {
    if (!confirm("Remove organizer permissions from this member?")) return;
    setBusy(true);
    try {
      await api.delete(`/manifest/moments/${momentId}/co-organizers/${caseId}`);
      await load();
      toast.success("Co-organizer removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't demote");
    } finally { setBusy(false); }
  };

  const completeIt = async (happened) => {
    // iter78 P0 fix + iter81 (Item P) - the browser `prompt()` returns
    // null when the user hits Cancel. We treat Cancel (null) as "abort
    // the whole action", and only treat an empty string (user hit OK
    // with blank) as "skip the attendee count".
    // iter81 (Item P): For Roommate-type Moments, we skip the attendee
    // prompt entirely - housing searches don't have attendees, and asking
    // "how many people attended?" after "Found a place" would be
    // nonsensical. The endpoint accepts a null attendee_count so this
    // is safe.
    const isRoommate = d?.moment?.type === "roommate";
    let attendee_count = null;
    if (happened && !isRoommate) {
      const raw = prompt("How many people actually attended? (leave blank to skip, Cancel to abort)");
      if (raw === null) return;    // user cancelled → do not complete
      const parsed = parseInt(raw, 10);
      if (raw !== "" && !isNaN(parsed)) attendee_count = parsed;
    }
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/complete`, { happened, attendee_count });
      onChanged(); onClose();
      toast.success(isRoommate ? "Search closed. Congrats." : "Thanks. Logged.");
    } catch (e) {
      toast.error("Couldn't complete");
    } finally { setBusy(false); }
  };

  // iter72 (Item 7) — Open-seat join request. Non-member submits an explicit
  // request instead of auto-joining via Going. Never auto-adds — organizer
  // must Accept.
  const requestSeat = async () => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/join-request`, {});
      await load();
      toast.success("Request sent. Awaiting organizer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send request.");
    } finally { setBusy(false); }
  };

  // Creator's decide action on a specific pending join request. Server does
  // the capacity re-check at accept time; we surface a specific toast if the
  // moment filled up in the meantime (409).
  const decideJoin = async (requestId, action) => {
    setBusy(true);
    try {
      await api.post(`/manifest/moments/${momentId}/join-requests/${requestId}/decide`, { action });
      await load();
      onChanged();
      toast.success(action === "accept" ? "Request accepted." : "Request declined.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete decision.");
    } finally { setBusy(false); }
  };

  if (!d) return null;
  const m = d.moment;
  const going = d.counts?.going ?? 0;
  const cap = m.capacity;
  const Icon = TYPE_META[m.type]?.icon || Sparkles;

  return (
    <div data-testid="moment-detail-modal" className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center p-3 sm:p-8 overflow-y-auto">
      <div className="w-full max-w-2xl moe-card p-6 md:p-8 bg-paper">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-elec font-bold">
              <Icon size={11} strokeWidth={2.4}/> {TYPE_META[m.type]?.label}
            </div>
            <h3 className="mt-1 text-xl md:text-2xl font-bold tracking-tight break-words">{m.title}</h3>
            <div className="mt-1 text-[12px] text-muted">
              by <span className="text-ink font-semibold">{d.creator?.display_name}</span> · created {_fmtDate(m.created_at)}
            </div>
            {/* iter73 (P0). For trip-type Moments, add an explicit product-
               boundary hint. Trips are typically started from Roam; when they
               land inside Manifest for coordination, users were confused why
               the modal appeared under the Manifest header. This line makes
               the Roam→Manifest handoff obvious. */}
            {m.type === "trip" && (
              <div className="mt-2 text-[11px] text-ink/70 leading-snug" data-testid="moment-trip-provenance">
                Trip Assembly. Discovery lives in Drift; coordination lives here in Cohort.
                {" "}
                <Link
                  to="/dashboard/packing"
                  data-testid="moment-trip-packing-link"
                  className="underline font-semibold text-elec hover:text-ink"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open your Smart Packing list
                </Link>
                .
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="moment-detail-close" className="text-muted hover:text-ink"><X size={18}/></button>
        </div>

        {d.recommendation && (
          <div className="mt-4 p-3 rounded-lg bg-[color:var(--moe-mint)] text-[12px] text-ink border border-elec/25 inline-flex items-start gap-2">
            <Sparkles size={12} strokeWidth={2.4} className="shrink-0 mt-0.5 text-elec"/>
            <span>{d.recommendation.reason}</span>
          </div>
        )}

        {m.description && (
          <div className="mt-4 text-sm text-ink/85 whitespace-pre-wrap leading-relaxed">{m.description}</div>
        )}

        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[13px]">
          <div className="p-3 rounded-lg border border-ink/10">
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">
              {m.type === "arrival" ? "Landing" : m.type === "roommate" ? "Move-in window" : "When"}
            </div>
            <div className="mt-1 inline-flex items-center gap-1.5">
              <CalendarClock size={12} strokeWidth={2.4} className="text-elec"/>
              {_fmtDate(m.start_date)}{m.end_date && m.end_date !== m.start_date ? ` – ${_fmtDate(m.end_date)}` : ""}
              {m.date_locked && <Lock size={11} strokeWidth={2.4} className="ml-1 text-elec"/>}
              {m.type === "arrival" && m.landing_time && (
                <span className="ml-1 text-[12px] font-semibold text-ink" data-testid="moment-detail-landing-time">· {m.landing_time}</span>
              )}
            </div>
            {/* iter92 (Item 2) — countdown line under the date. Only renders
               when there's real data (see _momentCountdown for the rules). */}
            {(() => {
              const cd = _momentCountdown(m);
              if (!cd) return null;
              return (
                <div data-testid={`moment-detail-countdown-${m.id}`}
                     className="mt-1 text-[11px] text-elec font-semibold">
                  {cd}
                </div>
              );
            })()}
          </div>
          <div className="p-3 rounded-lg border border-ink/10">
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Going</div>
            <div className="mt-1 text-lg font-bold text-ink" data-testid="moment-detail-capacity">
              {going}{cap ? ` of ${cap}` : ""}
              <span className="ml-2 text-[11px] font-normal text-muted">
                &middot; {d.counts.interested} interested &middot; {d.counts.declined} can't make it
              </span>
            </div>
          </div>
          {(m.budget_min != null || m.budget_max != null) && (
            <div className="p-3 rounded-lg border border-ink/10 sm:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Budget (per person)</div>
              <div className="mt-1 text-sm">{m.budget_min ?? "–"} – {m.budget_max ?? "–"}</div>
            </div>
          )}
          {/* iter81 (Item O) - Arrival-type flight details, only when the
             creator opted to share them. Empty fields simply don't render. */}
          {m.type === "arrival" && (m.airport || m.flight_number) && (
            <div className="p-3 rounded-lg border border-ink/10 sm:col-span-2" data-testid="moment-detail-arrival-fields">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Flight details</div>
              <div className="mt-1 text-sm flex flex-wrap gap-x-3 gap-y-1">
                {m.airport && <span data-testid="moment-detail-airport"><span className="text-muted">Airport:</span> <b className="text-ink">{m.airport}</b></span>}
                {m.flight_number && <span data-testid="moment-detail-flight-number"><span className="text-muted">Flight:</span> <b className="text-ink">{m.flight_number}</b></span>}
              </div>
            </div>
          )}
          {/* iter81 (Item P) - Roommate-type housing logistics. Same
             "only render populated fields" rule. */}
          {m.type === "roommate" && (m.budget_min_monthly != null || m.budget_max_monthly != null || m.preferred_area || m.roommates_sought || m.room_type) && (
            <div className="p-3 rounded-lg border border-ink/10 sm:col-span-2" data-testid="moment-detail-roommate-fields">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Housing search</div>
              <div className="mt-1 text-sm flex flex-wrap gap-x-3 gap-y-1">
                {(m.budget_min_monthly != null || m.budget_max_monthly != null) && (
                  <span data-testid="moment-detail-budget-monthly">
                    <span className="text-muted">Monthly budget:</span>{" "}
                    <b className="text-ink">€{m.budget_min_monthly ?? "?"} – €{m.budget_max_monthly ?? "?"}</b>
                  </span>
                )}
                {m.preferred_area && <span data-testid="moment-detail-preferred-area"><span className="text-muted">Area:</span> <b className="text-ink">{m.preferred_area}</b></span>}
                {m.roommates_sought && <span data-testid="moment-detail-roommates-sought"><span className="text-muted">Roommates sought:</span> <b className="text-ink">{m.roommates_sought}</b></span>}
                {m.room_type && (
                  <span data-testid="moment-detail-room-type"><span className="text-muted">Room type:</span> <b className="text-ink">{ROOM_TYPE_LABELS[m.room_type] || m.room_type}</b></span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Date voting */}
        {m.date_precision === "flexible" && (m.date_options || []).length > 0 && !m.date_locked && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-elec font-bold">Vote on the date</div>
            <div className="mt-2 grid gap-2">
              {m.date_options.map((opt) => (
                <div key={opt} className="flex items-center justify-between gap-3 p-2.5 border border-ink/10 rounded-lg">
                  <div className="text-sm">
                    <div className="font-semibold text-ink">{_fmtDate(opt)}</div>
                    <div className="text-[11px] text-muted">{d.vote_tally[opt] || 0} vote{d.vote_tally[opt] === 1 ? "" : "s"}{d.my_vote === opt ? " · your pick" : ""}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      data-testid={`moment-vote-${opt}`}
                      onClick={() => voteDate(opt)}
                      disabled={busy || d.my_vote === opt}
                      className={`px-3 py-1.5 text-[12px] font-semibold rounded-full border transition-colors disabled:opacity-50 ${d.my_vote === opt ? "bg-elec text-white border-elec" : "border-ink/20 hover:border-elec"}`}
                    >
                      {d.my_vote === opt ? "Voted" : "Vote"}
                    </button>
                    {d.is_organizer && (
                      <button
                        type="button"
                        data-testid={`moment-lock-${opt}`}
                        onClick={() => lockDate(opt)}
                        disabled={busy}
                        className="px-3 py-1.5 text-[12px] font-semibold rounded-full bg-ink text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <Lock size={11} strokeWidth={2.4}/> Lock
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members */}
        {d.members?.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Who's in</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {d.members.map((mem) => (
                <span key={mem.case_id} className={`text-[11px] px-2 py-1 rounded-full border ${
                  mem.state === "going" ? "bg-[color:var(--moe-mint)] border-elec/30" :
                  mem.state === "interested" ? "bg-[color:var(--moe-yolk)]/30 border-[color:var(--moe-yolk)]" :
                  "bg-ink/5 border-ink/15 text-muted line-through"
                }`}>
                  {mem.display_name} · {mem.state}
                  {d.co_organizers?.some(c => c.case_id === mem.case_id) && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-elec" data-testid={`co-organizer-badge-${mem.case_id}`}>· organizer</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* iter80 (Item N) - Co-organizer management. Only the creator sees
           this control. Any member currently `going` can be promoted; the
           creator alone can demote. Shared organizer permissions cover
           lock-date, cancel, complete, and join-request decisions. */}
        {d.is_creator && d.members?.some(mem => mem.state === "going" && mem.case_id !== d.creator?.case_id) && (
          <div className="mt-5 pt-4 border-t border-ink/10" data-testid="moment-co-organizers-block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Co-organizers</div>
            <div className="mt-1 text-[11px] text-muted leading-snug">
              Promote a going member to share organizer permissions (lock-date, cancel, complete, decide join requests).
            </div>
            <div className="mt-2 flex flex-col gap-1.5">
              {d.members
                .filter(mem => mem.state === "going" && mem.case_id !== d.creator?.case_id)
                .map(mem => {
                  const isOrg = d.co_organizers?.some(c => c.case_id === mem.case_id);
                  return (
                    <div key={mem.case_id} className="flex items-center gap-2 text-[12px]" data-testid={`co-organizer-row-${mem.case_id}`}>
                      <span className="text-ink font-semibold">{mem.display_name}</span>
                      {isOrg ? (
                        <button
                          type="button"
                          onClick={() => demoteCoOrganizer(mem.case_id)}
                          disabled={busy}
                          data-testid={`co-organizer-demote-${mem.case_id}`}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-ink/20 text-muted hover:text-ink hover:border-ink/40"
                        >
                          Remove organizer role
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => promoteCoOrganizer(mem.case_id)}
                          disabled={busy}
                          data-testid={`co-organizer-promote-${mem.case_id}`}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-elec/40 text-elec hover:bg-elec/10"
                        >
                          Make co-organizer
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Actions */}
        {["planning", "confirmed"].includes(m.status) && (
          <div className="mt-6 pt-4 border-t border-ink/10 flex flex-wrap gap-2">
            {/* iter72. When the caller has a PENDING join-request, hide the
                direct state buttons so the user isn't torn between two paths.
                A declined request leaves the state buttons visible (with the
                honest badge below) so they can still choose Interested/Decline. */}
            {!d.is_creator && d.my_join_request?.status !== "pending" && (
              <>
                <button data-testid="moment-set-going"     onClick={() => setState("going")}      disabled={busy || d.my_state === "going"} className="h-10 px-4 rounded-full bg-ink text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"><Check size={12} strokeWidth={2.4}/> Going</button>
                <button data-testid="moment-set-interested" onClick={() => setState("interested")} disabled={busy || d.my_state === "interested"} className="h-10 px-4 rounded-full border border-ink/25 text-[13px] font-semibold hover:border-elec disabled:opacity-50">Interested</button>
                <button data-testid="moment-set-declined"  onClick={() => setState("declined")}   disabled={busy || d.my_state === "declined"} className="h-10 px-4 rounded-full text-[13px] text-muted hover:text-ink disabled:opacity-50">Can't make it</button>
              </>
            )}
            {d.is_organizer && (() => {
              // iter78 P0 fix + iter81 (Item P) roommate lifecycle.
              // For Trip/Arrival/Social: "Mark completed" is a footgun on
              // future-dated moments so we demote it to a text link until
              // the trip actually ends.
              // For Roommate: housing searches are open-ended - the
              // organizer needs to be able to mark "Found a place"
              // whenever the search actually concludes, regardless of
              // move-in date. So we ALWAYS show the primary CTA for
              // roommates, with the roommate-specific label.
              const isRoommate = m.type === "roommate";
              const today = new Date().toISOString().slice(0, 10);
              const tripEnded = (m.end_date || "") <= today;
              const completeLabel = isRoommate ? "Mark as found a place" : "Mark completed";
              const cancelLabel = isRoommate ? "Close search" : "Cancel Moment";
              const showPrimary = isRoommate || tripEnded;
              return (
                <>
                  {showPrimary ? (
                    <button
                      data-testid="moment-complete-yes"
                      onClick={() => completeIt(true)}
                      disabled={busy}
                      className="h-10 px-4 rounded-full bg-ink text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50"
                    >{completeLabel}</button>
                  ) : (
                    <div className="flex flex-col gap-0.5" data-testid="moment-complete-deferred">
                      <button
                        data-testid="moment-complete-yes"
                        onClick={() => completeIt(true)}
                        disabled={busy}
                        className="self-start h-8 px-2 text-[12px] text-muted hover:text-ink underline decoration-dotted underline-offset-2 disabled:opacity-50"
                      >{completeLabel}</button>
                      <span className="text-[10px] text-muted leading-snug">
                        Available after the trip ends on {_fmtDate(m.end_date)}. Use only for a trip that has actually happened.
                      </span>
                    </div>
                  )}
                  <button
                    data-testid="moment-cancel"
                    onClick={cancel}
                    disabled={busy}
                    className="h-10 px-4 rounded-full text-[13px] text-[color:var(--moe-pink)] hover:opacity-80 disabled:opacity-50"
                  >{cancelLabel}</button>
                </>
              );
            })()}
          </div>
        )}

        {/* iter72 (Item 7). Open-seat join request for non-members.
            Shows only when:
              - Moment is active (planning|confirmed)
              - Caller is not the creator
              - Caller has NO going/interested/declined state (i.e. Isn't already a member)
              - AND either (capacity is set OR a join-request already exists for this caller)
            When capacity is unbounded and the caller has no request yet, the
            existing "Going" button is enough. We don't add a redundant CTA. */}
        {["planning", "confirmed"].includes(m.status) && !d.is_creator && !d.my_state && (m.capacity || d.my_join_request) && (
          <div className="mt-3" data-testid="moment-join-request-block">
            {!d.my_join_request && (
              <>
                <button
                  data-testid="moment-request-seat"
                  onClick={requestSeat}
                  disabled={busy}
                  className="h-10 px-4 rounded-full border border-elec text-elec text-[13px] font-semibold hover:bg-elec/10 disabled:opacity-50"
                >
                  Request a seat
                </button>
                <div className="mt-1 text-[11px] text-muted">
                  Organizer approves each request. You won't be added automatically.
                </div>
              </>
            )}
            {d.my_join_request?.status === "pending" && (
              <div
                data-testid="moment-join-request-pending"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[color:var(--moe-yolk)]/40 border border-[color:var(--moe-yolk)] text-[13px] text-ink font-semibold"
              >
                Request sent. Awaiting organizer
              </div>
            )}
            {d.my_join_request?.status === "declined" && (
              <div
                data-testid="moment-join-request-declined"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-ink/5 border border-ink/15 text-[13px] text-muted font-semibold"
              >
                Organizer declined your request
              </div>
            )}
          </div>
        )}

        {/* iter72 (Item 7). Creator-only pending join requests list.
            Rich profile card per requester (same shape as Manifest request
            cards from Item 4) plus Accept / Decline buttons. */}
        {d.is_organizer && (d.pending_join_requests?.length > 0) && (
          <div className="mt-6 pt-4 border-t border-ink/10" data-testid="moment-pending-join-requests">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
              Pending join requests · {d.pending_join_requests.length}
            </div>
            <div className="mt-3 grid gap-3">
              {d.pending_join_requests.map((jr) => (
                <MomentJoinRequestCard
                  key={jr.id}
                  jr={jr}
                  busy={busy}
                  onAccept={() => decideJoin(jr.id, "accept")}
                  onDecline={() => decideJoin(jr.id, "decline")}
                />
              ))}
            </div>
          </div>
        )}
        {/* iter79 (Item G) - Shareable link block. Available to creator +
           anyone who's going/interested so they can spread the Moment via
           WhatsApp. Copies /m/{id} to clipboard. */}
        {(d.is_creator || d.my_state === "going" || d.my_state === "interested") && (
          <div className="mt-6 pt-4 border-t border-ink/10" data-testid="moment-share-block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Share this Moment</div>
            <MomentShareButton momentId={momentId}/>
          </div>
        )}

        {/* iter79 (Item H) - Comment thread. Members-only (creator +
           going/interested). Server enforces the same gate on both endpoints. */}
        {(d.is_creator || d.my_state === "going" || d.my_state === "interested") && (
          <MomentComments momentId={momentId}/>
        )}
      </div>
    </div>
  );
}

// iter79 (Item G) + iter80 (Item J) - Share button. The share URL points
// at the backend `/api/m/{id}` HTML endpoint so WhatsApp/iMessage crawlers
// hit real per-Moment OG meta tags (the SPA route at /m/{id} is JS-only
// and can't be crawled). The backend HTML meta-refreshes browsers to
// /m/{id}, so humans still land on the SPA preview.
function MomentShareButton({ momentId }) {
  const [justCopied, setJustCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/m/${momentId}`;
  const humanUrl = `${window.location.origin}/m/${momentId}`;
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt("Copy this link:", shareUrl);
      }
      setJustCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setJustCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy - long-press the link below to copy manually");
    }
  };
  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={copy}
          data-testid="moment-share-copy"
          className="h-10 px-4 rounded-full bg-ink text-white text-[13px] font-semibold hover:opacity-90 inline-flex items-center gap-1.5"
        >
          {justCopied ? "Copied to clipboard" : "Copy share link"}
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent("Join this Moment on Ministry of Exchange: " + shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="moment-share-whatsapp"
          className="h-10 px-4 rounded-full border border-ink/25 text-[13px] font-semibold text-ink hover:border-elec inline-flex items-center gap-1.5"
        >
          Share on WhatsApp
        </a>
        <a
          href={`/api/moments/${momentId}/ics`}
          data-testid="moment-add-to-calendar"
          className="h-10 px-4 rounded-full border border-ink/25 text-[13px] font-semibold text-ink hover:border-elec inline-flex items-center gap-1.5"
        >
          Add to calendar
        </a>
      </div>
      <div
        data-testid="moment-share-url"
        className="mt-2 text-[11px] text-muted break-all select-all"
      >
        {humanUrl}
      </div>
      <div className="mt-1 text-[11px] text-muted leading-snug">
        Anyone with this link sees a preview (title, dates, first names). They must log in and opt into Manifest to actually join. Calendar file works in Google Calendar, Apple Calendar, and Outlook.
      </div>
    </>
  );
}

// iter79 (Item H) - Simple comment thread. Refresh on post, poll every 15s.
function MomentComments({ momentId }) {
  const [comments, setComments] = useState(null);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  // iter97 VV — report a specific comment
  const [reportTarget, setReportTarget] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/manifest/moments/${momentId}/comments`);
      setComments(r.data.comments || []);
    } catch (e) {
      setComments([]);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [momentId]);

  const post = async (e) => {
    e.preventDefault();
    if (!body.trim() || posting) return;
    setPosting(true);
    try {
      const r = await api.post(`/manifest/moments/${momentId}/comments`, { body: body.trim() });
      setComments((cs) => [...(cs || []), r.data]);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post");
    } finally { setPosting(false); }
  };

  return (
    <div className="mt-6 pt-4 border-t border-ink/10" data-testid="moment-comments">
      <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Discussion</div>
      <div className="mt-1 text-[11px] text-muted leading-snug">
        Visible only to Moment members. Intent-only - no addresses, no pooled money, no phone numbers.
      </div>
      <div className="mt-3 flex flex-col gap-2" data-testid="moment-comments-list">
        {comments === null && <div className="moe-shimmer h-10 w-full"/>}
        {comments !== null && comments.length === 0 && (
          <div className="text-[12px] text-muted italic" data-testid="moment-comments-empty">
            No comments yet. Start the coordination.
          </div>
        )}
        {(comments || []).map((c) => (
          <div key={c.id} className="rounded-xl border border-ink/10 bg-paper p-3 group" data-testid={`moment-comment-${c.id}`}>
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className={`text-[12px] font-semibold ${c.by?.is_me ? "text-elec" : "text-ink"}`}>{c.by?.display_name || "Member"}</span>
                <span className="text-[10px] text-muted">{_fmtDate((c.created_at || "").slice(0, 10))}</span>
              </div>
              {!c.by?.is_me && !c.by?.author_deleted && c.by?.case_id && (
                <button
                  onClick={() => setReportTarget({ commentId: c.id, reportedCaseId: c.by.case_id })}
                  data-testid={`moment-comment-report-${c.id}`}
                  title="Report this comment"
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted hover:text-[color:var(--moe-pink)] transition-opacity"
                >
                  <Flag size={12}/>
                </button>
              )}
            </div>
            <div className="mt-1 text-[13px] text-ink leading-snug whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>
      <form onSubmit={post} className="mt-3 flex flex-col gap-2" data-testid="moment-comment-composer">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          placeholder="Add a note for members - e.g. meeting at the station at 9?"
          className="w-full min-h-[64px] rounded-lg border border-ink/15 bg-paper p-2 text-[13px] focus:outline-none focus:border-elec"
          data-testid="moment-comment-body"
        />
        <div className="flex items-center justify-between text-[11px] text-muted">
          <span>{body.length}/500</span>
          <button
            type="submit"
            disabled={!body.trim() || posting}
            data-testid="moment-comment-post"
            className="h-9 px-4 rounded-full bg-ink text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-40"
          >
            {posting ? "Posting..." : "Post"}
          </button>
        </div>
      </form>

      {reportTarget && (
        <MomentReportDialog
          momentId={momentId}
          commentId={reportTarget.commentId}
          reportedCaseId={reportTarget.reportedCaseId}
          onClose={() => setReportTarget(null)}
        />
      )}
    </div>
  );
}

function MomentReportDialog({ momentId, commentId, reportedCaseId, onClose }) {
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("/manifest/report", {
        reported_case_id: reportedCaseId,
        moment_id: momentId,
        moment_comment_id: commentId,
        reason, details,
      });
      setSent(true);
      setTimeout(onClose, 1600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to submit report.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4" data-testid="moment-report-dialog">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Report this comment</div>
        {!sent ? (
          <>
            <h3 className="mt-2 text-lg font-bold">What happened?</h3>
            <div className="mt-1 text-[12px] text-muted leading-snug">
              Moderators can only view this Moment's thread after a report is filed.
            </div>
            <div className="mt-4 space-y-2">
              {["harassment","inappropriate","spam","other"].map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="reason" checked={reason===r} onChange={() => setReason(r)} data-testid={`moment-report-reason-${r}`} />
                  <span className="capitalize">{r}</span>
                </label>
              ))}
            </div>
            <textarea
              data-testid="moment-report-details"
              value={details} onChange={(e) => setDetails(e.target.value)}
              placeholder="Optional details for the moderators"
              rows={3}
              maxLength={1000}
              className="mt-3 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm focus:outline-none focus:border-elec"
            />
            {err && <div className="mt-2 text-sm text-[color:var(--moe-pink)]">{err}</div>}
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={onClose} className="moe-btn !py-2 text-xs border border-ink/25">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="moment-report-submit" className="moe-btn moe-btn-primary !py-2 text-xs">
                {busy ? "Submitting…" : "Submit report"}
              </button>
            </div>
          </>
        ) : (
          <div className="text-sm text-ink mt-3">Thanks. Moderators will review this shortly.</div>
        )}
      </div>
    </div>
  );
}

// iter72 (Item 7) — Rich profile card for a pending join request, rendered
// inside the DetailModal for the creator. Same field discipline as Manifest
// Item 4 request cards: real DB fields, "Not shared" for empties.
function MomentJoinRequestCard({ jr, busy, onAccept, onDecline }) {
  const q = jr.requester || {};
  const startLabel = q.start_date ? _fmtDate(q.start_date) : null;
  const durLabel   = q.duration_days ? `${q.duration_days}d` : null;
  return (
    <div className="moe-card p-4" data-testid={`moment-join-request-${jr.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-elec/10 text-elec font-bold flex items-center justify-center shrink-0 text-sm">
          {q.display_name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-ink text-sm truncate">{q.display_name || "Student"}</div>
          <div className="text-[11px] text-muted truncate">
            {q.host_university || q.display_city || "Host details not shared"}
          </div>
        </div>
      </div>

      <dl
        data-testid={`moment-join-request-details-${jr.id}`}
        className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]"
      >
        <MomentJoinDetailRow label="Destination" value={q.host_city ? `${q.host_city}, ${q.country}` : (q.country || null)} />
        <MomentJoinDetailRow label="Dates" value={startLabel && durLabel ? `${startLabel} · ${durLabel}` : startLabel || null} />
        <MomentJoinDetailRow label="Home university" value={q.home_university || null} />
        <MomentJoinDetailRow label="Program" value={q.program || null} />
      </dl>
      {q.bio ? (
        <div className="mt-3 text-[12px] text-ink/80 leading-snug border-l-2 border-elec/40 pl-3">
          {q.bio}
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-ink/50 italic">Bio not shared.</div>
      )}
      {jr.note && (
        <div
          data-testid={`moment-join-request-note-${jr.id}`}
          className="mt-3 rounded-lg bg-[color:var(--moe-mint)]/50 border border-elec/25 p-3 text-[12px] text-ink leading-relaxed"
        >
          <div className="text-[9px] uppercase tracking-widest text-elec font-bold mb-1">Note</div>
          {jr.note}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-ink/10 flex gap-2">
        <button
          type="button"
          data-testid={`moment-join-request-accept-${jr.id}`}
          onClick={onAccept}
          disabled={busy}
          className="flex-1 moe-btn moe-btn-primary !py-2 text-xs disabled:opacity-50"
        >
          <Check size={12}/> Accept
        </button>
        <button
          type="button"
          data-testid={`moment-join-request-decline-${jr.id}`}
          onClick={onDecline}
          disabled={busy}
          className="flex-1 moe-btn !py-2 text-xs border border-ink/25 disabled:opacity-50"
        >
          <X size={12}/> Decline
        </button>
      </div>
    </div>
  );
}

function MomentJoinDetailRow({ label, value }) {
  const isSet = !!value;
  return (
    <div className="min-w-0">
      <dt className="text-[9px] uppercase tracking-widest text-muted font-bold">{label}</dt>
      <dd className={`text-[11px] truncate ${isSet ? "text-ink" : "text-ink/40 italic"}`}>
        {isSet ? value : "Not shared"}
      </dd>
    </div>
  );
}
